import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { selectUser } from "app/slice/selectors";
import { useIsAppViewActive } from "app/hooks/use-app-view-mode";
import { useGlobalSlice } from "app/slice";
import { StatusBadge } from "./status-badge";
import { StatusBadgeSkeleton } from "./status-badge-skeleton";
import type { AccountStatusViewModel } from "./hooks/useAccountStatus";

const routeLabels = {
  dashboard: "Dashboard",
  "payment-history": "Payment History",
  "manage-profile": "Manage Profile",
  "manage-cards": "Manage Cards",
  "change-password": "Change Password",
  "sim-service": "SIM Service",
};

interface MyAccountHeaderProps {
  accountStatus: AccountStatusViewModel;
}

export const MyAccountHeader = ({ accountStatus }: MyAccountHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const isAppView = useIsAppViewActive();
  const { isActive, isLoading } = accountStatus;
  const routeHistoryRef = useRef<string[]>(["/my-account/dashboard"]);
  const currentRoute = location.pathname.split("/").pop();
  const displayName =
    user?.firstName || user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : "Guest User";

  // Update vle-user with latest profile data
  const dispatch = useDispatch();
  const { useGetProfileQuery, actions: globalActions } = useGlobalSlice();
  const customerId = user?._id || user?.customerId;
  const { data: profileData } = useGetProfileQuery(
    { customerId },
    { skip: !customerId, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    const apiIsFixedPhone = profileData?.data?.isFixedPhone;
    const currentIsFixedPhone = user?.isFixedPhone;

    if (
      apiIsFixedPhone !== undefined &&
      apiIsFixedPhone !== currentIsFixedPhone
    ) {
      dispatch(
        globalActions.updateUserPartial({
          isFixedPhone: apiIsFixedPhone,
        })
      );
    }
  }, [profileData, user?.isFixedPhone, dispatch, globalActions]);

  // Track route changes and store route history
  useEffect(() => {
    const currentPath = location.pathname;

    // Only track my-account routes
    if (currentPath.includes("/my-account/")) {
      // Add current route to history if it's different from the last one
      if (
        routeHistoryRef.current[routeHistoryRef.current.length - 1] !==
        currentPath
      ) {
        const lastRoute =
          routeHistoryRef.current[routeHistoryRef.current.length - 1];

        // If navigating from dashboard to another my-account page, ensure dashboard is in history
        // This handles the case when navigating from MobileLineDetails (which is still on dashboard route)
        if (
          lastRoute === "/my-account/dashboard" &&
          currentPath !== "/my-account/dashboard"
        ) {
          // Dashboard is already in history, just add the new route
          routeHistoryRef.current.push(currentPath);
        } else if (
          currentPath !== "/my-account/dashboard" &&
          lastRoute !== "/my-account/dashboard"
        ) {
          // If we're navigating between non-dashboard pages, check if dashboard should be added
          // This handles the case when navigating from MobileLineDetails view
          const hasDashboardInHistory = routeHistoryRef.current.includes(
            "/my-account/dashboard"
          );
          if (!hasDashboardInHistory) {
            // Add dashboard before the new route if it's not in history
            routeHistoryRef.current.push("/my-account/dashboard");
          }
          routeHistoryRef.current.push(currentPath);
        } else {
          // Normal case: just add the route
          routeHistoryRef.current.push(currentPath);
        }

        // Keep only last 10 routes to prevent memory issues
        if (routeHistoryRef.current.length > 10) {
          routeHistoryRef.current = routeHistoryRef.current.slice(-10);
        }
      }
    }
  }, [location.pathname]);

  const handleBackNavigation = () => {
    const routeHistory = routeHistoryRef.current;

    // Find the previous route in history
    let previousRoute = "/my-account/dashboard"; // Default fallback

    // Look for the previous route in history that's different from current
    for (let i = routeHistory.length - 2; i >= 0; i--) {
      const route = routeHistory[i];
      if (route !== location.pathname && route.includes("/my-account/")) {
        // Check if this route is from routeLabels
        const isFromRouteLabels = Object.keys(routeLabels).some((routeKey) =>
          route.includes(`/my-account/${routeKey}`)
        );

        if (isFromRouteLabels) {
          previousRoute = route;
          break;
        }
      }
    }

    // Navigate to the previous route
    navigate(previousRoute);
  };

  return (
    <React.Fragment>
      {routeLabels[currentRoute as keyof typeof routeLabels] !==
        "Dashboard" && (
        // && !isAppView 
        <div className="sm:hidden h-[2.25rem]">
          <div
            className={`fixed left-0 right-0 z-[10] flex gap-2 items-center bg-white px-5 py-3 border-b border-[#DDE4EC] ${
              isAppView === "hidden" ? "top-0" : "top-16"
            }`}
          >
            <img
              src="/assets/outline-chevron-left.svg"
              alt="Back"
              className="cursor-pointer shrink-0 sm:hidden"
              onClick={handleBackNavigation}
              tabIndex={0}
              aria-label="Go back to previous screen"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleBackNavigation();
                }
              }}
            />
            <span className="inline-block text-black text-base font-semibold leading-[1.4rem]">
              {routeLabels[currentRoute as keyof typeof routeLabels] ||
                "My Account"}
            </span>
          </div>
        </div>
      )}
      <div
        className={`px-5 md:px-6 xl:px-7 2xl:px-[1.875rem] ${
          routeLabels[currentRoute as keyof typeof routeLabels] === "Dashboard"
            ? "hidden sm:block"
            : "hidden sm:block"
        }`}
      >
        <div className="flex-shrink-0 flex justify-between items-center gap-2 sm:gap-3 sm:border-b border-[#E9EAEB] pb-2 sm:pb-3 lg:pb-4">
          <h1 className="text-lg sm:text-xl md:text-[1.375rem] lg:text-2xl font-extrabold sm:font-bold leading-6 sm:leading-7 text-black font-mobile">
            Welcome, {displayName}
          </h1>
          {(currentRoute === "dashboard" ||
            currentRoute === "sim-service") &&
            (isLoading ? (
              <StatusBadgeSkeleton />
            ) : isActive !== null ? (
              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2.5">
                <p className="hidden sm:block text-[0.775rem] sm:text-[0.825rem] md:text-sm font-medium leading-5 md:leading-6 text-black">
                  Account Status
                </p>
                <StatusBadge isActive={isActive} />
              </div>
            ) : null)}
        </div>
      </div>
    </React.Fragment>
  );
};
